import { Button, Collapse, Grid, Tooltip } from 'antd';
import { ArrowLeft, ArrowRight, ArrowUp, Download, Printer } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFullComparableBoats, useGetActiveCarouselItem, useSetActiveCarouselItem } from '../hooks/useCompare';
import styles from './CompareTable.module.scss';
import { CompareTableProps } from './CompareTable.props';
import DesignerSpecsPanelData from './components/DesignerSpecsPanelData.component';
import EngineSpecsPanelData from './components/EngineSpecsPanelData.component';
import GeneralSpecsPanelData from './components/GeneralSpecsPanelData.component';
import MainSpecsPanelData from './components/MainSpecsPanelData.component';
import RigSpecsPanelData from './components/RigSpecsPanelData.component';
import StorageSpecsPanelData from './components/StorageSpecsPanelData.component';

const { Panel } = Collapse;

const CompareTable: FC<CompareTableProps> = () => {
  const elementRef = useRef(null);
  const [distanceFromTop, setDistanceFromTop] = useState(0);
  const [expandedPanels, setExpandedPanels] = useState<string[]>([
    'main',
    'general',
    'designer',
    'rig&sails',
    'engine',
    'storage',
  ]);

  const activeCarouselItem = useGetActiveCarouselItem();
  const windowInnerWidth = window.innerWidth;

  const handleScroll = useCallback(() => {
    const element = elementRef.current;

    if (element) {
      const rect = element.getBoundingClientRect();
      const distance = rect.top + window.pageYOffset;

      setDistanceFromTop(distance);
    }
  }, []);

  useEffect(() => {
    // Initial calculation
    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const setActiveCarouselItem = useSetActiveCarouselItem();

  const { data, isFetched } = useFullComparableBoats();
  const { xs, md } = Grid.useBreakpoint();

  const handleCollapseAll = useCallback(() => {
    setExpandedPanels(
      expandedPanels.length === 0 ? ['main', 'general', 'designer', 'rig&sails', 'engine', 'storage'] : [],
    );
  }, [expandedPanels.length]);

  const compareBoatsLength = useMemo(() => {
    let dif: number;

    if (windowInnerWidth >= 1440) {
      dif = 7;
    }

    if (windowInnerWidth < 1440 && windowInnerWidth >= 1200) {
      dif = 6;
    }

    if (windowInnerWidth < 1200 && windowInnerWidth >= 1024) {
      dif = 5;
    }

    if (windowInnerWidth < 1024 && windowInnerWidth >= 992) {
      dif = 4;
    }

    if (windowInnerWidth < 992) {
      dif = 3;
    }

    return data?.length - dif;
  }, [data?.length]);

  return (
    <div className={styles.compareTableWrapper}>
      <div className={styles.tableHeaderContainer}>
        <div>
          <p className={styles.tableSpecsText}>Specs</p>
        </div>
        <div className={styles.tableHeaderExtra}>
          <Button onClick={handleCollapseAll}>
            {expandedPanels.length === 0 ? 'Expand all' : xs ? '' : 'Collapse all'}
          </Button>
          <Button icon={<Printer size={16} />} onClick={() => window.print()}>
            {xs ? '' : 'Print'}
          </Button>
          <Tooltip title="Coming soon">
            <Button icon={<Download size={16} />} disabled>
              {xs ? '' : 'Export'}
            </Button>
          </Tooltip>
        </div>
      </div>

      {isFetched ? (
        <div>
          <Collapse
            className={styles.tableCollapseContainer}
            activeKey={expandedPanels}
            onChange={(keys) => setExpandedPanels(keys as string[])}
          >
            <Panel header="Main" key="main">
              <MainSpecsPanelData boatComparableData={data} />
            </Panel>

            <Panel header="General" key="general">
              <GeneralSpecsPanelData boatComparableData={data} />
            </Panel>

            <Panel header="Designer" key="designer">
              <DesignerSpecsPanelData boatComparableData={data} />
            </Panel>

            <Panel header="Rig & Sails" key="rig&sails">
              <RigSpecsPanelData boatComparableData={data} />
            </Panel>
            <Panel header="Engine" key="engine">
              <EngineSpecsPanelData boatComparableData={data} />
            </Panel>
            <Panel header="Storage" key="storage">
              <StorageSpecsPanelData boatComparableData={data} />
            </Panel>
          </Collapse>

          <Button
            icon={<ArrowUp size={16} />}
            shape="circle"
            style={distanceFromTop > 600 ? { marginRight: '8px' } : { marginRight: '8px', display: 'none' }}
            onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }}
            className={styles.upArrowButton}
            ref={elementRef}
          />

          {md && compareBoatsLength > 0 && (
            <div className={styles.sliderChangeButtonsContainer}>
              <Button
                icon={<ArrowLeft size={16} />}
                shape="circle"
                style={{ marginRight: '8px' }}
                disabled={activeCarouselItem < 0.9 ? true : false}
                onClick={() => {
                  setActiveCarouselItem(activeCarouselItem - 1);
                }}
              />

              <Button
                icon={<ArrowRight size={16} />}
                shape="circle"
                disabled={activeCarouselItem >= compareBoatsLength ? true : false}
                onClick={() => {
                  setActiveCarouselItem(activeCarouselItem + 1);
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <Collapse className={styles.tableCollapseContainer}>
          <Panel header="Main" key="main" />

          <Panel header="General" key="general" />

          <Panel header="Designer" key="designer" />

          <Panel header="Rig & Sails" key="rig&sails" />
          <Panel header="Engine" key="engine" />
          <Panel header="Storage" key="storage" />
          {/* <Panel header="Performance & Ratios" key="performance&ratios" /> */}
        </Collapse>
      )}
    </div>
  );
};

export { CompareTable };
